import { atom } from 'jotai';
import { createResourceAtom, mutationAtom } from 'lib/atoms';
import request from 'lib/request';
import type { Response } from 'lib/request/types';
import type {
  CostDetailResponse,
  CostLifecycleResponse,
  CostOverviewResponse,
} from '../types-cost';
import { buildSectionMutationAtom } from 'lib/atoms/section-mutation';

// ---------------------------------------------------------------------------
// Cost section data layer. Mirrors `atoms/wells.ts`:
//   • the Overview tiles + per-well table live in a module-level atom so the
//     overview and any mutation can share one piece of state;
//   • the per-well Structure Costs detail uses the codebase `createResourceAtom`
//     factory (auto-fetches on mount, setter refetches);
//   • edit / confirm / remove are uniform { url, method, data } mutation atoms.
// ---------------------------------------------------------------------------

// ── Overview (GET /documents/:documentId/afe_structuring/costs) ─────────────

export const costOverviewDataAtom = atom<CostOverviewResponse | null>(null);
export const costOverviewLoadingAtom = atom(false);

export const fetchCostOverviewAtom = atom(
  null,
  async (_get, set, href: string | undefined) => {
    if (!href) return;
    set(costOverviewLoadingAtom, true);
    try {
      const response = await request.get<CostOverviewResponse>(href);
      if ('error' in response) {
        console.error('Failed to fetch AFE cost overview:', response.error);
      } else {
        set(costOverviewDataAtom, response);
      }
    } catch (error) {
      console.error('Failed to fetch AFE cost overview:', error);
    } finally {
      set(costOverviewLoadingAtom, false);
    }
  }
);

export const costOverviewUrl = (documentId: number | string) =>
  `/documents/${documentId}/afe_structuring/costs`;

// ── Structure Costs detail (GET .../costs/:wellId — wellId = afeHeaderId) ────

export const createCostDetailResourceAtom = (href: string | undefined) =>
  createResourceAtom<CostDetailResponse>(href);

export const costDetailUrl = (
  documentId: number | string,
  wellId: number | string
) => `/documents/${documentId}/afe_structuring/costs/${wellId}`;

// ── Rerun Cost Extraction (POST .../costs/:wellId/cost_extraction_rerun) ──────
//
// Deterministically re-derives this header's cost line items from its staged
// well's citations and REPLACES the active scenario's line items (discards
// manual edits). The endpoint re-renders the refreshed cost detail, so the
// mutation resolves to CostDetailResponse (one round-trip, no separate refetch).

export const costExtractionRerunUrl = (
  documentId: number | string,
  wellId: number | string
) =>
  `/documents/${documentId}/afe_structuring/costs/${wellId}/cost_extraction_rerun`;

// ── Working Interest edit (PATCH /afe_headers/:afeHeaderId) ─────────────────
//
// The cost `wellId` IS the afeHeaderId, so WI lives on the AfeHeader, not a
// structuring endpoint. This is the existing AfeHeadersController#update, which
// permits `afe_header[:working_interest_percentage]` and renders the full
// AfeHeader detail. We ignore the response body and re-read the cost detail to
// pick up the server-recomputed Net AFE Cost.

export const afeHeaderUrl = (afeHeaderId: number | string) =>
  `/afe_headers/${afeHeaderId}`;

// ── Lifecycle / edit mutations ──────────────────────────────────────────────

/** Edit line items in place (PATCH .../costs/:wellId). */
export const editCostAtom = buildSectionMutationAtom<CostDetailResponse>();

/** Rerun cost extraction (POST .../costs/:wellId/cost_extraction_rerun). */
export const rerunCostAtom = buildSectionMutationAtom<CostDetailResponse>();

/** Mark cost as correct (POST resources.confirmation.href). */
export const confirmCostAtom =
  buildSectionMutationAtom<CostLifecycleResponse>();

/** Remove correct status (DELETE resources.confirmation.href). */
export const revertCostAtom = buildSectionMutationAtom<CostLifecycleResponse>();

// ── Remove / Undo the well from the cost panel ──────────────────────────────
//
// The cost detail exposes `afeTargetId`, so the cost header's Remove/Undo reuse
// the Well-section lifecycle endpoints (one source of truth for well removal).

export const removeWellFromCostAtom = buildSectionMutationAtom<unknown>();
export const restoreWellFromCostAtom = buildSectionMutationAtom<unknown>();

export const wellRemovalUrl = (
  documentId: number | string,
  afeTargetId: number
) => `/documents/${documentId}/afe_structuring/wells/${afeTargetId}`;

export const wellRestorationUrl = (
  documentId: number | string,
  afeTargetId: number
) =>
  `/documents/${documentId}/afe_structuring/wells/${afeTargetId}/restoration`;

/**
 * Edit Working Interest % on the AfeHeader (PATCH /afe_headers/:afeHeaderId).
 *
 * AfeHeadersController#update renders the AfeHeader detail on success and
 * `{ errors: { working_interest_percentage: [...] } }` on validation failure —
 * a different error envelope than the structuring `{ error }` shape — so this
 * is its own atom rather than the generic `buildSectionMutationAtom`.
 */
interface AfeHeaderEditCallbacks {
  onSuccess?: () => void;
  onError?: (message?: string) => void;
}

// Shared core for the AfeHeader inline edits (Working Interest, Held Flag). Both
// PATCH /afe_headers/:id with a partial `afe_header` body and surface the same
// `{ errors: {...} }` envelope, so they share one request + callback path and
// only differ in the body + the validation-error copy.
const patchAfeHeader = async (
  afeHeaderId: number | string,
  body: Record<string, unknown>,
  callbacks: AfeHeaderEditCallbacks,
  errorMessage: string
): Promise<Response<unknown>> => {
  const controller = new AbortController();
  const response = await request.patch<unknown, { errors?: unknown }>(
    afeHeaderUrl(afeHeaderId),
    { afe_header: body },
    controller
  );

  if (response.type === 'success') {
    callbacks.onSuccess?.();
  } else if (response.type === 'error') {
    callbacks.onError?.(errorMessage);
  } else if (response.type === 'fatal') {
    callbacks.onError?.(response.message);
  }
  return response;
};

interface WorkingInterestArg extends AfeHeaderEditCallbacks {
  afeHeaderId: number | string;
  workingInterestPercentage: number | null;
}

export const editWorkingInterestAtom = mutationAtom<
  Response<unknown>,
  [WorkingInterestArg]
>((_get, _set, arg): Promise<Response<unknown>> =>
  patchAfeHeader(
    arg.afeHeaderId,
    { working_interest_percentage: arg.workingInterestPercentage },
    arg,
    'Failed to update working interest.'
  )
);

interface HeldFlagArg extends AfeHeaderEditCallbacks {
  afeHeaderId: number | string;
  /** Raw held_flag enum ('held' / 'submittal'); null clears it (None). */
  heldFlag: string | null;
}

export const editHeldFlagAtom = mutationAtom<Response<unknown>, [HeldFlagArg]>(
  (_get, _set, arg): Promise<Response<unknown>> =>
    patchAfeHeader(
      arg.afeHeaderId,
      { held_flag: arg.heldFlag },
      arg,
      'Failed to update held flag.'
    )
);
